import { useEffect, useMemo, useState } from 'react';
import { type IntlShape } from 'react-intl-next';
import { useSnapshot } from 'valtio';

import { i18n } from './i18n-state';
import { messages } from './messages';

type TimeGradiation = {
  unit: Intl.RelativeTimeFormatUnit;
  /** Number of seconds to have passed for this unit to apply (closest one matches) */
  threshold: number;
  /** Smallest # to group unit by, defaults to `1` */
  unitGranularity?: number;
  unitConversionToSeconds: number;
  customFormat?: (date: Date, locale: string) => string;
  customFormatType?: 'relative' | 'absolute';
};

/**
 * Maps to the different units supported by Intl.RelativeTimeFormat for displaying
 * relative times.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/RelativeTimeFormat/format#parameters
 */
const timeGradiations: TimeGradiation[] = [
  // From 0 seconds - 60 seconds (rounded down to the closest multiple of 5)
  {
    unit: 'second',
    threshold: 0,
    unitGranularity: 5,
    unitConversionToSeconds: 1,
  },
  // From 1 minute - 5 minutes
  {
    unit: 'minute',
    threshold: 60,
    unitConversionToSeconds: 60,
  },
  // From 5 minutes - 60 minutes (rounded down to the closest multiple of 5)
  {
    unit: 'minute',
    threshold: 5 * 60,
    unitGranularity: 5,
    unitConversionToSeconds: 60,
  },
  // From 1 hour - 24 hours
  {
    unit: 'hour',
    threshold: 60 * 60,
    unitConversionToSeconds: 60 * 60,
  },
  {
    unit: 'day',
    threshold: 24 * 60 * 60,
    unitConversionToSeconds: 24 * 60 * 60,
    customFormatType: 'absolute',
    customFormat: (date, locale) => {
      return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
  },
];

const findClosestTimeGrade = (secondsDifference: number) => {
  let i = timeGradiations.length - 1;
  while (Math.abs(secondsDifference) < timeGradiations[i].threshold && i > 0) {
    i--;
  }

  return timeGradiations[i];
};

const parseDate = (dateString: string | null | undefined) => {
  if (!dateString) {
    return undefined;
  }

  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return undefined;
  }

  return date;
};

const getSecondsDifferenceFromNow = (date: Date) => (date.getTime() - Date.now()) / 1000;

/**
 * Returns relative time for given dateString, eg: "10 seconds ago", "2
 * hours ago". This relative time is localised for the user's locale.
 *
 * Caution: Because our i18n state is initialized async, pay mind to when this is
 * called.
 *
 * If using this in React, use the `useFormatRelativeTime` hook below which will
 * automatically update if/when the i18n state is updated
 *
 * If using outside of React, you can subscribe to the i18n valtio state and
 * re-run `formatRelativeTime` to return the relative time in the new locale
 */
export const formatRelativeTime = (
  dateArg: Date | string | undefined | null,
  intl: IntlShape = i18n.intl,
) => {
  const date = typeof dateArg === 'string' ? parseDate(dateArg) : dateArg;

  if (!date) {
    return undefined;
  }

  const secondsDifference = getSecondsDifferenceFromNow(date);

  // Show "now" for very recent times (within 5 seconds)
  if (Math.abs(secondsDifference) < 5) {
    return intl.formatMessage(messages.now);
  }

  const timeGrade = findClosestTimeGrade(secondsDifference);

  if (timeGrade.customFormat) {
    return timeGrade.customFormat(date, intl.locale);
  }

  const unitGranularity = timeGrade.unitGranularity ?? 1;

  // Converts seconds difference to closest time grade unit - preserves
  // direction (time in past or time in future) and applies specified
  // granularity
  const unitDifference =
    Math.sign(secondsDifference) *
    Math.floor(
      Math.abs(secondsDifference) / timeGrade.unitConversionToSeconds / unitGranularity,
    ) *
    unitGranularity;

  return intl.formatRelativeTime(unitDifference, timeGrade.unit);
};

/** React hook version of `formatRelativeTime`
 *  Automatically re-renders relative time based on closest unit (eg every 5
 *  seconds, every 5 minutes, every hour, etc.)
 * */
export const useFormatRelativeTime = (dateString: string | null | undefined) => {
  const i18nSnapshot = useSnapshot(i18n);
  const date = useMemo(() => parseDate(dateString), [dateString]);
  const [relativeTime, setRelativeTime] = useState<string>();

  // This state variable is used to force React to re-render since there are no actual state changes to
  // trigger an update (we want to update the relative timestamp after X seconds)
  const [renderCount, setRenderCount] = useState<number>(0);

  useEffect(() => {
    if (!date) {
      return;
    }

    setRelativeTime(formatRelativeTime(date, i18nSnapshot.intl as IntlShape));

    const secondsDiff = getSecondsDifferenceFromNow(date);
    const timeGrade = findClosestTimeGrade(secondsDiff);

    // Skip refreshing time if it's absolute format
    if (timeGrade.customFormatType === 'absolute') {
      return;
    }

    const refreshInterval =
      (timeGrade.unitGranularity ?? 1) * timeGrade.unitConversionToSeconds;

    // Calculate number of seconds we should wait until we re-calculate the time
    const nextUpdateWaitTime = refreshInterval - Math.abs(secondsDiff % refreshInterval);

    // Increment our state variable to force a re-render to update the relative time
    const timeout = setTimeout(() => {
      setRenderCount((c) => c + 1);
    }, nextUpdateWaitTime * 1000);

    return () => clearTimeout(timeout);
  }, [date, renderCount, i18nSnapshot.intl]);

  return relativeTime;
};
